import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { AddButton, Card, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import BudgetItemsGroup from "@src/components/modules/expenses/Create/components/BudgetItemsCard/BudgetItemsGroup";
import { OcrBeta } from "@src/components/modules/expenses/Create/components/OcrBeta";
import type { TBudgetItemForm } from "@src/components/modules/expenses/Create/form";
import { Icon } from "@src/components/ui-kit/Icon";
import { useStore as useAppStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { getQueryParamOptional } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect } from "react";
import { type Store, useStore } from "../../store";
import BudgetItem from "./BudgetItem";
import type { BudgetItemStore } from "./BudgetItemStore";

const useInitBudgetItemAmount = (store: Store) => {
  useEffect(() => {
    // Preset budget item when its the only one
    if (store.form.expense_items.size !== 1) return;

    const expenseItemForm = store.form.expense_items.values().next().value
      ?.form as TBudgetItemForm;
    // Don't overwrite existing item
    if (!expenseItemForm.$.id.value) {
      expenseItemForm.$.amount_in_currency.onChange(
        expenseItemForm.$.amount_in_currency.value.setDenomValue(
          store.totalAmountForExpense,
        ),
      );
    }
  }, [store.totalAmountForExpense]);
};

export const BudgetItemsCard = observer(function BudgetItemsCard() {
  const { authStore, workspaceStore } = useAppStore();
  const store = useStore();
  const { form } = store;
  const { query } = useRouter();

  useEffect(() => {
    const brandId = getQueryParamOptional("brandId", query);
    const projectId = getQueryParamOptional("projectId", query);
    const budgetItemId = getQueryParamOptional("budgetItemId", query);
    const autoApprove =
      (workspaceStore.settings?.automatically_approved_expense_items ??
        false) &&
      !!budgetItemId;

    if (brandId && projectId) {
      form.expense_items.clear();
      form.addExpenseItem(
        new NumberModel(0, workspaceStore.settings?.currency.denomination!),
        {
          brand_id: brandId,
          project_id: projectId,
          budget_item_id: budgetItemId,
          budget_item_type: budgetItemId
            ? form.getItemTypeFromId(budgetItemId)
            : undefined,
          approved: autoApprove,
          responsible_id: autoApprove
            ? (authStore.user?.id ?? undefined)
            : undefined,
        },
        true,
      );
    }
    if (!store.form.expense_items.size) {
      store.createBudgetItem();
    }
  }, []);

  useInitBudgetItemAmount(store);

  return (
    <Fragment>
      <Card pos="relative" w="full">
        <Card.Section>
          <Grid
            pos="relative"
            alignItems="start"
            templateColumns={{
              base: "1fr",
              md: "1fr 1fr",
            }}
            w="full"
            pb="4"
          >
            <Flex pos="relative">
              <Flex align="center">
                <Text as="h2" mr="2" fontSize="xl">
                  <Trans>Budget items</Trans>
                </Text>
                <AddButton onClick={store.createBudgetItem} />
              </Flex>
            </Flex>
            <Flex
              justify="flex-end"
              direction={{ base: "column", md: "row" }}
              gap="3"
              mt={{ base: "2", md: "unset" }}
            >
              {!store.predictionBudgetItemsCheck &&
                !!store.activePredictionFile &&
                !store.activePredictionBudgetItemsFilled && (
                  <Button
                    pos={{ base: "sticky", md: "unset" }}
                    left={{ base: "6", md: "unset" }}
                    w="fit-content"
                    isDisabled={store.predictionBudgetItemsFillInProgress}
                    onClick={() => store.predictionFillBudgetItems()}
                    size="sm"
                    variant="subtle"
                  >
                    <Trans>Extract items from document</Trans>
                  </Button>
                )}
              <Checkbox
                w="fit-content"
                isChecked={store.predictionBudgetItemsCheck}
                onChange={({ target }) => {
                  store.predictionBudgetItemsCheck = target.checked;
                }}
              >
                <Text fontSize="sm">
                  <Trans>Automatically extract items</Trans>
                  &nbsp;
                  <OcrBeta hideLabel />
                </Text>
              </Checkbox>
            </Flex>
          </Grid>
          {!!store.form.expenseItemsForm.formError && (
            <div className="mb-8">
              <Alert status="error">
                <AlertIcon />
                {store.form.expenseItemsForm.formError}
              </Alert>
            </div>
          )}
          {!!store.plannedExpenseMappingsError && (
            <div className="mb-8">
              <Alert status="error">
                <AlertIcon />
                {store.plannedExpenseMappingsError}
              </Alert>
            </div>
          )}
          {store.predictionBudgetItemsFillInProgress ? (
            <VStack
              align="center"
              justify="center"
              gap="4"
              w={{ base: "90vw", md: "full" }}
              h="136px"
            >
              <Spinner color="teal.500" size="xl" thickness="3px" />
              <Text fontSize="large">
                <Trans>Extracting data, please wait...</Trans>
              </Text>
            </VStack>
          ) : (
            <Box
              overflowX={{
                base: "auto",
                md: "visible",
              }}
              overflowY="visible"
              maxW="full"
            >
              <Box w={{ base: "1140px", md: "full" }}>
                <Grid
                  gap="3"
                  templateColumns={store.budgetItemsColumns}
                  w="full"
                  mx="auto"
                  color="gray.500"
                  fontSize="xs"
                  fontWeight="600"
                >
                  <GridItem colSpan={3}>
                    <Text fontWeight="inherit">
                      <Trans>Brand / Project / Budget item</Trans>
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="inherit">
                      <Trans>Amount</Trans>
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="inherit">
                      <Trans>Responsible</Trans>
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="inherit">
                      <Trans>Approved</Trans>
                    </Text>
                  </GridItem>
                </Grid>
                {Array.from(store.form.expense_items)
                  .toReversed()
                  .filter(
                    ([, item]) =>
                      !item.form.$.id.value ||
                      !store.hasBrand(item.brandIdForGrouping),
                  )
                  .map(([key, item], index, array) => (
                    <Fragment key={key}>
                      <BudgetItem id={key} itemStore={item} />
                      {index < array.length - 1 && <Divider my="3" />}
                    </Fragment>
                  ))}
                {store.expenseItemStructured?.map((itemStructured, index) => {
                  const brandId = itemStructured.brand?.id;
                  const filteredExpenseItems = Array.from(
                    store.form.expense_items,
                  ).filter(
                    ([, item]: [string, BudgetItemStore]) =>
                      !!item.form.$.id.value &&
                      item.brandIdForGrouping === brandId,
                  );

                  if (filteredExpenseItems.length === 0) return null;

                  return (
                    <BudgetItemsGroup
                      key={`budget-item-group-${itemStructured.brand?.id ?? index}`}
                      expenseItems={filteredExpenseItems}
                      brandName={itemStructured.brand?.name ?? ""}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Card.Section>
      </Card>
      <Modal
        isOpen={store.expenseItemDescriptionModalState.isOpen}
        onClose={store.expenseItemDescriptionModalState.close}
        size="2xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Expense item descriptions</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxH="full">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Title</Trans>
                  </Th>
                  <Th w="100px">
                    <Trans>Actions</Trans>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {store.descriptionRows.length > 0 ? (
                  store.descriptionRows.map(({ id, field }) => {
                    const isUpdating = store.updatingDescriptionIds.has(id);
                    const isEditing = store.editingDescriptionIds.has(id);

                    return (
                      <Tr key={id}>
                        <Td>
                          {isEditing ? (
                            <TextField
                              value={field.value}
                              error={field.error}
                              onChange={field.onChange}
                            />
                          ) : (
                            <Text>{field.value}</Text>
                          )}
                        </Td>
                        {isEditing ? (
                          <Td>
                            <HStack spacing="2">
                              <IconButton
                                aria-label={t`Confirm edit description`}
                                colorScheme="green"
                                icon={<Icon name="check" />}
                                isLoading={isUpdating}
                                onClick={async () => {
                                  const validation = await field.validate();
                                  if (validation.hasError) return;
                                  store.handleConfirmEditDescription(
                                    id,
                                    validation.value,
                                  );
                                }}
                                size="sm"
                                variant="subtle"
                              />
                              <IconButton
                                ml="1"
                                aria-label={t`Cancel edit description`}
                                colorScheme="red"
                                icon={<Icon name="x-close" />}
                                isLoading={isUpdating}
                                onClick={() => {
                                  store.handleCancelEditDescription(id);
                                }}
                                size="sm"
                                variant="subtle"
                              />
                            </HStack>
                          </Td>
                        ) : (
                          <Td>
                            <HStack spacing="2">
                              <IconButton
                                aria-label={t`Edit description`}
                                icon={<Icon name="edit-02" />}
                                isLoading={isUpdating}
                                onClick={() => {
                                  store.handleEditDescription(id);
                                }}
                                size="sm"
                                variant="ghost"
                              />
                              <IconButton
                                aria-label={t`Delete description`}
                                colorScheme="red"
                                icon={<Icon name="trash-03" />}
                                isLoading={isUpdating}
                                onClick={() => {
                                  store.handleDeleteDescription(id);
                                }}
                                size="sm"
                                variant="ghost"
                              />
                            </HStack>
                          </Td>
                        )}
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={2}>
                      <Text color="gray.600" textAlign="center">
                        <Trans>No descriptions yet. Create one below.</Trans>
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <HStack align="flex-start" justify="flex-end" w="full" spacing="2">
              <Box>
                <TextField
                  onChange={store.newDescriptionField.onChange}
                  value={store.newDescriptionField.value}
                  placeholder={t`Enter new description`}
                  error={store.newDescriptionField.error}
                />
              </Box>
              <Center minH="9">
                <Button
                  isLoading={store.isCreatingDescription}
                  leftIcon={<Icon name="plus" />}
                  onClick={store.handleCreateDescription}
                  variant="link"
                >
                  <Trans>Add new description</Trans>
                </Button>
              </Center>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Fragment>
  );
});
